import { useEffect, useState } from 'react';
import { applyPhoneAttribute, detectPhone, shouldResnapshot, type Insets, type PhoneDetection, type PhoneViewport } from './phoneLayout';

/** Reads env(safe-area-inset-*) from a probe, since JS has no API for it. */
function readInsets(): Insets {
  if (typeof document === 'undefined') return { top: 0, right: 0, bottom: 0, left: 0 };
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const out = {
    top: parseFloat(cs.paddingTop) || 0,
    right: parseFloat(cs.paddingRight) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0,
  };
  probe.remove();
  return out;
}

export interface PhoneSnapshot extends PhoneViewport {
  /** The detection and every input to it, for the editorDebug overlay. */
  detection: PhoneDetection;
}

function snapshot(): PhoneSnapshot {
  const detection = detectPhone();
  applyPhoneAttribute(detection.phone);
  if (typeof window === 'undefined') {
    return { phone: false, landscape: false, w: 0, h: 0, insets: { top: 0, right: 0, bottom: 0, left: 0 }, detection };
  }
  /* Spec 13.12: the visible viewport, which Safari's toolbars shrink and
     grow, rather than the window. Not while the page is pinch-zoomed or the
     keyboard is up, when the window is what the layout keeps. */
  const vv = window.visualViewport;
  const useVisible = !!vv && Math.abs(vv.scale - 1) < 0.01 && !document.documentElement.hasAttribute('data-keyboard-open');
  const w = useVisible ? Math.round(vv!.width) : window.innerWidth;
  const h = useVisible ? Math.round(vv!.height) : window.innerHeight;
  return {
    phone: detection.phone,
    landscape: w > h,
    w,
    h,
    insets: detection.phone ? readInsets() : { top: 0, right: 0, bottom: 0, left: 0 },
    detection,
  };
}

/* Decide before the first render that reads it, so the phone: classes are
   right on the first paint. The editor's components load with this module. */
if (typeof window !== 'undefined') applyPhoneAttribute(detectPhone().phone);

/**
 * The phone viewport, as a snapshot that changes only when it should
 * (shouldResnapshot). Detection is re-run on resize and orientationchange,
 * debounced, and the attribute the phone: variant reads is rewritten each
 * time. After an orientation change it is read again at 400 and 900ms, since
 * the final size can arrive without another event.
 */
export function usePhoneLayout(): PhoneSnapshot {
  const [v, setV] = useState<PhoneSnapshot>(snapshot);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const later: ReturnType<typeof setTimeout>[] = [];
    let rotatedAt = 0;
    const update = () => {
      setV((prev) => {
        const next = snapshot();
        return shouldResnapshot(prev, next, Date.now() - rotatedAt < 1000) ? next : prev;
      });
    };
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(update, 150);
    };
    const rotated = () => {
      rotatedAt = Date.now();
      debounced();
      later.push(setTimeout(update, 400), setTimeout(update, 900));
    };
    window.addEventListener('resize', debounced);
    window.addEventListener('orientationchange', rotated);
    /* Safari's toolbars showing or hiding change the visible viewport
       without always resizing the window (spec 13.12). */
    window.visualViewport?.addEventListener('resize', debounced);
    window.visualViewport?.addEventListener('scroll', debounced);
    return () => {
      if (timer) clearTimeout(timer);
      later.forEach(clearTimeout);
      window.removeEventListener('resize', debounced);
      window.removeEventListener('orientationchange', rotated);
      window.visualViewport?.removeEventListener('resize', debounced);
      window.visualViewport?.removeEventListener('scroll', debounced);
    };
  }, []);
  return v;
}
